import styled from "@emotion/styled";
import { BarChart, Map } from "@mui/icons-material";
import { Box, ListItemDecorator, Stack, TabList, TabPanel, Tabs } from "@mui/joy";
import { Outlet, SearchSchemaInput, createFileRoute } from "@tanstack/react-router";
import { memo, useEffect, useRef } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useShallow } from "zustand/react/shallow";

import { TabLink } from "@/components/Links";
import { useRoutesUiStore } from "@/components/RoutesProvider";
import { useRoutesTableStore } from "@/components/RoutesTable/RoutesProvider2";

import Analyse from "../../../components/Analyse/Analyse";
import Maps from "../../../components/Maps";
import { OrientationModal } from "../../../components/OrientationModal";
import RouteDetailSidebar from "../../../components/RouteDetailSidebar/RouteDetailSidebar";
import { RoutesScreenProvider } from "../../../components/RoutesProvider/RoutesProvider";
import ContextMenu from "../../../components/RoutesTable/ContextMenu";
import { FilterSidebar } from "../../../components/RoutesTable/FilterSidebar/FilterSidebar";
import RoutesTable from "../../../components/RoutesTable/RoutesTable";

export const StyledPanelResizeHandleVertical = styled(PanelResizeHandle)`
  height: 0px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    transform: translateY(-50%);
    height: 4px;
    z-index: 10;
    opacity: 0;
    background-color: var(--joy-palette-primary-blue1);
    transition: opacity 0.2s;
  }
  &[data-resize-handle-state="hover"]:after,
  &[data-resize-handle-state="drag"]:after {
    opacity: 1;
  }
`;

export const StyledPanelResizeHandleHorizontal = styled(PanelResizeHandle)`
  width: 0px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    transform: translateX(-50%);
    width: 4px;
    z-index: 10;
    opacity: 0;
    background-color: var(--joy-palette-primary-blue1);
    transition: opacity 0.2s;
  }
  &[data-resize-handle-state="hover"]:after,
  &[data-resize-handle-state="drag"]:after {
    opacity: 1;
  }
`;

const RoutesScreenContent = memo(() => {
  const { tab } = Route.useSearch();

  const { displayRouteSidebar, displayFilterSidebar } = useRoutesUiStore();

  // useEffect(() => {
  //   if (!sidebarRef.current || !stackRef.current) {
  //     return;
  //   }
  //   const defaultSize = 350;
  //   // get default size as a percentage of the parent container
  //   const defaultSizePercentage =
  //     (100 * defaultSize) / stackRef.current.offsetWidth;
  //   sidebarRef.current.resize(defaultSizePercentage);
  //   // sidebarRef.expand();
  // }, [displayRouteSidebar, displayFilterSidebar]);

  return (
    <Tabs value={tab}>
      <OrientationModal />
      <Stack direction="row" height={"100dvh"}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <TabList sx={{ justifyContent: "center" }}>
            <TabLink
              value="maps"
              sx={{ width: 0.35, minWidth: 100, height: 40 }}
              from={Route.fullPath}
              to="."
              search={(prev) => ({ ...prev, tab: "maps" })}
            >
              <ListItemDecorator>
                <Map />
              </ListItemDecorator>
              Maps
            </TabLink>
            <TabLink
              value="analyse"
              sx={{ width: 0.35, minWidth: 100, height: 40 }}
              from={Route.fullPath}
              to="."
              search={(prev) => ({ ...prev, tab: "analyse" })}
            >
              <ListItemDecorator>
                <BarChart />
              </ListItemDecorator>
              Analyse
            </TabLink>
          </TabList>
          <PanelGroup direction="vertical" style={{ flex: 1 }}>
            <Panel>
              <TabPanel value="maps" sx={{ padding: 0, height: "100%" }}>
                <Maps />
              </TabPanel>
              <TabPanel value="analyse" sx={{ padding: 0, height: "100%" }}>
                <Analyse />
              </TabPanel>
            </Panel>
            <StyledPanelResizeHandleVertical />
            <Panel>
              <RoutesTable />
            </Panel>
          </PanelGroup>
        </Stack>
        <Box
          sx={{
            width: "350px",
            display: displayRouteSidebar || displayFilterSidebar ? "block" : "none",
          }}
        >
          {/* keep the route detail sidebar mounted so we keep the form state */}
          <RouteDetailSidebar hidden={displayFilterSidebar} />
          {displayFilterSidebar ? <FilterSidebar /> : null}
        </Box>
      </Stack>
      <ContextMenu />
    </Tabs>
  );
});

RoutesScreenContent.displayName = "RoutesScreenContent";

const SyncSearchWithFilterParams = () => {
  const { recentlyRestored } = Route.useSearch();
  const [filterParams, setFilterParam] = useRoutesTableStore(
    useShallow((state) => [state.filterParams, state.setFilterParam]),
  );
  const navigate = Route.useNavigate();

  const ignoreSearchChangeRef = useRef(false);
  const ignoreFilterChangeRef = useRef(false);

  const filterParamsRef = useRef(filterParams);
  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  useEffect(() => {
    if (ignoreSearchChangeRef.current) {
      ignoreSearchChangeRef.current = false;
      return;
    }

    if (recentlyRestored && !filterParamsRef.current.isRecentlyRestored) {
      ignoreFilterChangeRef.current = true;
      setFilterParam("isRecentlyRestored", true);
    }
  }, [recentlyRestored, setFilterParam]);

  useEffect(() => {
    if (ignoreFilterChangeRef.current) {
      ignoreFilterChangeRef.current = false;
      return;
    }

    if (!filterParams.isRecentlyRestored && recentlyRestored) {
      ignoreSearchChangeRef.current = true;
      navigate({ search: (prev) => ({ ...prev, recentlyRestored: false }) });
    }
  }, [filterParams.isRecentlyRestored, recentlyRestored, navigate]);

  return null;
};

const RoutesScreen = () => {
  return (
    <RoutesScreenProvider>
      <SyncSearchWithFilterParams />
      <RoutesScreenContent />
      <Outlet />
    </RoutesScreenProvider>
  );
};

type RoutesSearchParams = {
  tab?: "maps" | "analyse";
  recentlyRestored?: boolean;
};

export const Route = createFileRoute("/gym/$venueUuid/routes/$climbType")({
  component: RoutesScreen,
  validateSearch: (search?: Record<string, unknown> & SearchSchemaInput): RoutesSearchParams => {
    return {
      tab: search?.tab === "maps" || search?.tab === "analyse" ? search.tab : "maps",
      recentlyRestored: !!search?.recentlyRestored || undefined,
    };
  },
  parseParams: (params) => {
    return {
      climbType: params.climbType.toUpperCase(),
    };
  },
  stringifyParams: (params) => {
    return {
      climbType: params.climbType.toLowerCase(),
    };
  },
  errorComponent: ({ error }) => {
    throw error;
  },
});
